/*
  NOVAPIX STORE-SPECIFIC cart invariants: the Mystery Gift set, pack sold
  quantities (see PACKS), the companion lines that ride with a handset (see
  COMPANIONS), and the clear cart control that Shrine does not ship (see
  clearCart, near the bottom).

  THE RULE, in one place
  ----------------------
  A cart holds exactly one complete Mystery Gift set, or neither. Never both,
  never a partial set, never a duplicate piece.

      P = total quantity of Flex Panel variants in the cart

      P >= 2  ->  the free three-piece set   (paid pair removed)
      P == 1  ->  the paid two-piece set, only if the customer asked for it
      P == 0  ->  no gift pieces at all

      A = quantity of the Lightning Adapter, L = quantity of handset units on
                                             lines carrying Connection=Lightning
      A == L, always
      C = quantity of the USB-C Charging Cable, B = quantity of Bluetooth handsets
      C == B, always
      M = quantity of the Power Brick
      M == B, always   (companionUpdates, below; COMPANIONS lists all three rules)

  Companion counts are read from the SERVER cart on every pass, not from the
  pending bundle targets. That is safe because handset rows are Shrine's own
  steppers, never bundle rows: a handset change goes through Shrine's fetch,
  the wrapper observes it, and reconcile then reads the already-updated cart.

  The required set is derived from THE CART, not from the on-screen widget.
  That is deliberate: a stale tab, a restored session, the back button and a
  hand-edited cart are exactly the cases where the widget and the cart disagree,
  and the cart is the thing that gets charged.

  Reconciliation is one pass that computes the whole desired state and sends a
  single /cart/update.js. That endpoint adds line items whose variant is not yet
  in the cart and removes them at quantity 0, so wrong-set removal, duplicate
  clearing and missing-piece addition all happen atomically. There are no
  per-interaction conditionals anywhere else; every entry point calls reconcile.

  It runs on EVERY cart mutation, by observing fetch rather than by hooking a
  particular theme event, so a removal made anywhere still restores the set.

  Loaded globally from layout/theme.liquid: the cart can be changed from any
  template, and the invariant has to hold on all of them.
*/
(function () {
  'use strict';

  var PANELS = [42863014281301, 42863014314069, 42863014346837, 42863014248533];
  var PAID = [42875561705557, 42875561738325];          // "1 of 2", "2 of 2", $10 each
  var FREE = [42875561607253, 42875561640021, 42875561672789]; // "1 of 3".."3 of 3", zeroed by the automatic discount
  var ALL_GIFT = PAID.concat(FREE);

  /* PACK SOLD VARIANTS: bought as a pack, fulfilled as units.
     The supplier ships one roll per unit of the tape SKU, so an order asking
     for one unit ships one roll against a listing that promises three. The
     variant is priced per roll ($3.33), which is why three units come to the
     advertised $9.99 with no discount and no price fiddling anywhere.

     Shrine cannot express this: its product form hardcodes
     items[n][quantity] = 1 for every ticked upsell and offers no setting, so
     the quantity is corrected here, on the same pass as the gift.

     The variant id is repeated in snippets/cart-drawer.liquid and
     sections/main-cart-items.liquid, which drop the quantity stepper for these
     lines. A test parses it back out of all three and fails if they diverge. */
  var PACKS = { 42863035711573: 3 }; // Extra Panel Tape, 3 rolls

  /* LIGHTNING ADAPTER: one adapter per Lightning handset, always.

     Lightning is NOT a variant. The supplier cannot fulfil a Lightning
     handset as its own SKU, so the product page sells Lightning as the
     matching 3.5 mm variant carrying the line item property
     Connection=Lightning, and this rule counts handset UNITS on the lines
     that carry it. Shopify keeps lines with different properties apart even
     for the same variant id, so a plain 3.5 mm handset and a Lightning one
     of the same colour sit on two lines and only the second earns an
     adapter. There is no trigger id table any more: the rule never looks at
     the variant id, only at the property.

     The key and the value below are mirrored, literal for literal, in
     assets/novapix-handset.js (which writes the property into the product
     form's /cart/add body), snippets/cart-drawer.liquid and
     sections/main-cart-items.liquid (which read it to print "Lightning /
     Colour"); tests/ids.test.js fails if any of them diverges. Matched
     EXACTLY: no trimming, no case folding. Shopify returns the property byte
     for byte and every writer is this theme, so a near miss is a bug to
     surface, not a value to forgive.

     The product page adds the adapter together with the handset, but the
     count is derived from THE CART on every pass, like the gift set, so a
     stale tab, a restored session, the back button and a hand-edited cart
     all converge on the next mutation. The adapter carries its real admin
     price ($10.00) and is zeroed at checkout by the automatic discount
     "Free Lightning Adapter", a 100% product discount with no trigger: the
     theme never touches price. A Buy X Get Y was ruled out by decision,
     because on this plan every product in a BXGY is ineligible for any
     other product discount, which would strip the handset's own pack
     discounts, and a BXGY could not key on the Lightning property anyway.
     Residual: a hand-built cart permalink for a lone adapter bypasses this
     script and checks out at the discounted price. The adapter id is
     repeated in snippets/cart-drawer.liquid and
     sections/main-cart-items.liquid (locked row, no remove control);
     tests/ids.test.js fails if they diverge. */
  var LIGHTNING_PROPERTY = 'Connection';
  var LIGHTNING_VALUE = 'Lightning';
  var ADAPTER = 42977976254549;

  /* USB-C CHARGING CABLE: one cable per Bluetooth handset, always.
     Bluetooth handsets are the six ids below (product 7546878885973). The
     product page adds the cable together with the handset; the count is
     derived from THE CART on every pass, exactly like the adapter. The cable
     is a real $10.00 product zeroed by the automatic discount "Free USB-C
     Charging Cable" rather than priced $0.00 in the admin. A Buy X Get Y was
     ruled out: on this plan every product in a BXGY is ineligible for any
     other product discount, which would strip the handset's own pack
     discounts. Residual: a hand-built cart permalink for a lone cable
     bypasses this script and checks out at the discounted price. The cable
     id is repeated in snippets/cart-drawer.liquid, sections/main-cart-items.liquid
     (locked row, no remove control, option lines suppressed) and
     assets/novapix-handset.js (first add); tests/ids.test.js fails if they
     diverge. */
  var BLUETOOTH = [
    42978006433877, // Bluetooth / Black
    42978006466645, // Bluetooth / Sky Blue
    42978006499413, // Bluetooth / Pink
    42978006532181, // Bluetooth / Red
    42978006564949, // Bluetooth / White
    42978006597717  // Bluetooth / Yellow
  ];
  var CABLE = 42978005057621;

  /* POWER BRICK: the second cable, one per Bluetooth handset,
     always. Same trigger set, same counting, same pricing model as the
     USB-C cable: a real $10.00 product (7550933696597) zeroed by the
     automatic discount "Free Mini USB Charging Cable", with the same
     permalink residual. Its id is repeated in snippets/cart-drawer.liquid,
     sections/main-cart-items.liquid (locked row, no remove control, option
     lines suppressed) and assets/novapix-handset.js (first add);
     tests/ids.test.js fails if they diverge. */
  var POWER_BRICK = 42986064150613;

  /* COMPANION RULES. Each entry is one invariant of the form "companion
     quantity equals trigger quantity", evaluated in order into the same
     updates map by companionUpdates. A rule is { name, match, companion }:
     match(line) says whether a cart line is a trigger, and the trigger
     quantity is the summed quantity of the lines it accepts (countWhere).
     The adapter rule matches by the Connection=Lightning property
     (isLightningLine); the two cable rules match by variant id (variantIn)
     and share the Bluetooth trigger set, each writing only its own
     companion key. Companions must be distinct from each other and from
     every trigger (no chaining), so two entries never write the same key;
     companionUpdates also refuses to count a rule's own companion line as
     a trigger, whatever properties it carries. The adapter rule stays first
     and the Power Brick rule last so the order of touched keys is stable.
     tests/ids.test.js asserts the ids are distinct and that the adapter
     rule keys on the property. */
  function isLightningLine(line) {
    var props = line.properties;
    return !!props && props[LIGHTNING_PROPERTY] === LIGHTNING_VALUE;
  }

  function variantIn(list) {
    return function (line) { return list.indexOf(line.variant_id) !== -1; };
  }

  var ADAPTER_RULE = { name: 'adapter', match: isLightningLine, companion: ADAPTER };
  var CABLE_RULE = { name: 'cable', match: variantIn(BLUETOOTH), companion: CABLE };
  var POWER_BRICK_RULE = { name: 'brick', match: variantIn(BLUETOOTH), companion: POWER_BRICK };
  var COMPANIONS = [ADAPTER_RULE, CABLE_RULE, POWER_BRICK_RULE];

  var CART_MUTATIONS = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var SECTIONS = 'cart-drawer,cart-icon-bubble';

  /* The /cart page's items table and footer are template-scoped sections whose
     ids only exist on that page (each element carries them in data-id). When
     present they are requested and repainted too, so the bundle stepper works
     in place there instead of forcing a reload per click. */
  function pageSection(elementId) {
    var el = document.getElementById(elementId);
    return el && el.dataset ? el.dataset.id : null;
  }

  function sectionsParam() {
    var extra = [pageSection('main-cart-items'), pageSection('main-cart-footer')]
      .filter(Boolean);
    return extra.length ? SECTIONS + ',' + extra.join(',') : SECTIONS;
  }

  // Does the customer want the paid set? STICKY, not single-use.
  //
  //   true  -> asked for it
  //   false -> declined it
  //   null  -> unknown, e.g. any page without the buy box. Falls back to
  //            passive upkeep: keep a complete pair, clear a partial one.
  //
  // Stickiness is the whole point. An earlier version passed the intent into a
  // single reconcile call scheduled with setTimeout(0), which raced Shrine's
  // still-in-flight panel add: reconcile saw a cart with no panels yet, decided
  // no gift was due, and deleted the pair it had just added. Holding the flag
  // means whenever reconcile eventually runs, it already knows the answer, so
  // ordering stops mattering.
  var paidIntent = null;

  // Lets the buy box untick its checkbox when reconcile decides the customer
  // opted out by deleting a piece.
  var onPaidCleared = null;

  var running = false;
  var rerun = false;
  /* Our own requests must not retrigger the observer. Marked per request
     rather than with a global flag: a global flag also hid any SHRINE
     mutation that started while our write was in flight (a stepper click in
     that window), so its reconcile never ran. A WeakSet keeps the marker on
     the request object itself and lets the wrapper tell the two apart. */
  var ownRequests = typeof WeakSet === 'function' ? new WeakSet() : null;
  var internal = false; // fallback for browsers without WeakSet

  /* Pending absolute totals for bundle rows, variant id -> quantity. Written
     by the stepper and by setVariantQuantity, consumed by the next reconcile
     pass, which folds them into ITS single /cart/update.js: the panel change,
     the gift correction it causes, and any pack correction all land in one
     write instead of two or three. */
  var bundleTargets = {};

  function ids(list, id) {
    return list.indexOf(id) !== -1;
  }

  function linesFor(cart, id) {
    var out = [];
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === id) out.push(cart.items[i]);
    }
    return out;
  }

  function qtyOf(cart, id) {
    return linesFor(cart, id).reduce(function (n, l) { return n + l.quantity; }, 0);
  }

  /* Total quantity in the cart of every variant in `list`, across lines. */
  function countOf(cart, list) {
    return list.reduce(function (n, id) { return n + qtyOf(cart, id); }, 0);
  }

  /* Total quantity across the lines `match` accepts. This is how a rule's
     trigger quantity is read: by property for the adapter, by variant id
     for the cable (see COMPANIONS). */
  function countWhere(cart, match) {
    var n = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (match(cart.items[i])) n += cart.items[i].quantity;
    }
    return n;
  }

  function panelCount(cart) {
    return countOf(cart, PANELS);
  }

  function lightningCount(cart) {
    return countWhere(cart, isLightningLine);
  }

  function bluetoothCount(cart) {
    return countOf(cart, BLUETOOTH);
  }

  /* Companion quantity must equal the trigger quantity (adapter == handset
     units on lines carrying Connection=Lightning, cable == Bluetooth
     handsets). Pure: takes the cart, the updates map being built and one
     COMPANIONS entry, writes the corrections into the map, and returns the
     keys it touched so a rejected write can be resent without them. The
     companion's own lines are never triggers, whatever they carry. Absent
     + wanted adds by variant id (the only key that can add); present lines
     are addressed by line key and collapsed onto the first one. A total
     that already matches is a no-op, never a rewrite, or a split line
     would cost a write and a repaint on every pass. */
  function companionUpdates(cart, updates, rule) {
    var want = countWhere(cart, function (line) {
      return line.variant_id !== rule.companion && rule.match(line);
    });
    var lines = linesFor(cart, rule.companion);
    var touched = [];
    if (!lines.length) {
      if (want > 0) {
        updates[rule.companion] = want;
        touched.push(String(rule.companion));
      }
      return touched;
    }
    var current = lines.reduce(function (n, l) { return n + l.quantity; }, 0);
    if (current === want) return touched;
    lines.forEach(function (line, index) {
      var target = index === 0 ? want : 0;
      if (line.quantity !== target) {
        updates[line.key] = target;
        touched.push(line.key);
      }
    });
    return touched;
  }

  /* Named entry points for the three rules. tests/cart-rules.test.js and
     tools/cart-matrix.js call these by name. */
  function adapterUpdates(cart, updates) {
    return companionUpdates(cart, updates, ADAPTER_RULE);
  }

  function cableUpdates(cart, updates) {
    return companionUpdates(cart, updates, CABLE_RULE);
  }

  function powerBrickUpdates(cart, updates) {
    return companionUpdates(cart, updates, POWER_BRICK_RULE);
  }

  /* Which set this cart must contain. Pure function of cart + intent.
     `sideEffects` is only passed by reconcile, so the function stays safe to
     call for a plain read. `panelsOverride` lets reconcile evaluate the rule
     against the panel count AS IT WILL BE after a pending bundle edit, so the
     gift change rides the same write as the panel change. */
  function requiredSet(cart, wants, sideEffects, panelsOverride) {
    var panels = panelsOverride == null ? panelCount(cart) : panelsOverride;
    if (panels >= 2) return FREE;

    if (panels === 1) {
      var present = PAID.filter(function (id) { return qtyOf(cart, id) > 0; }).length;

      if (wants === true) {
        // Absent entirely means this is the add: put both pieces in.
        // Exactly one piece means the customer deleted the other, which is read
        // as cancelling, so the intent is dropped and the remainder cleared.
        if (present === 1) {
          if (sideEffects) {
            paidIntent = false;
            if (typeof onPaidCleared === 'function') onPaidCleared();
          }
          return [];
        }
        return PAID;
      }

      if (wants === false) return [];

      // unknown: keep a complete pair, clear a partial one
      return present === PAID.length ? PAID : [];
    }

    return [];
  }

  function route(path) {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    return root + path;
  }

  function request(url, body) {
    var init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    };
    if (ownRequests) ownRequests.add(init); else internal = true;
    return fetch(url, init)
      .then(function (r) { return r.json(); })
      .finally(function () { if (!ownRequests) internal = false; });
  }

  function getCart() {
    return fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // ------------------------------------------- scroll and focus preservation

  /* The drawer has TWO scroll containers, split by viewport height:
     .cart-drawer__body (overflow:auto) on normal screens, .drawer__inner
     (overflow:scroll) under a max-height 650px media query. Every cart render,
     ours or Shrine's, replaces .drawer__inner's innerHTML wholesale, which
     DESTROYS the body scroller outright: its position dies with the node.
     Restoring only .drawer__inner (an earlier version of this fix) therefore
     did nothing on normal screens.

     So the snapshot is generic: .drawer__inner itself plus every descendant
     that is actually scrolled, each recorded as a re-findable selector, and
     the position is stamped back onto whichever element carries the class
     after the swap. */
  function drawerScroller() {
    return document.querySelector('.drawer__inner');
  }

  function scrollSelectorFor(el) {
    if (el.id) return '#' + el.id;
    var cls = String(el.className || '').split(/\s+/).filter(Boolean)[0];
    return cls ? '.drawer__inner .' + cls : null;
  }

  function snapshotScrolls() {
    var entries = [];
    var inner = drawerScroller();
    if (!inner) return entries;
    if (inner.scrollTop > 0) {
      entries.push({ selector: '.drawer__inner', top: inner.scrollTop });
    }
    var nodes = inner.querySelectorAll('*');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].scrollTop > 0) {
        var selector = scrollSelectorFor(nodes[i]);
        if (selector) entries.push({ selector: selector, top: nodes[i].scrollTop });
      }
    }
    return entries;
  }

  /* `onlyIfClamped` is the deferred (Shrine render) path: a customer who
     scrolled somewhere new mid-request keeps their position; only an element
     sitting at 0 that was captured above 0 is put back. The same-task path
     after our own swap restores unconditionally, because the recreated
     element always starts at 0. */
  function restoreScrolls(entries, onlyIfClamped) {
    for (var i = 0; i < entries.length; i++) {
      var el = document.querySelector(entries[i].selector);
      if (!el) continue;
      if (onlyIfClamped && el.scrollTop !== 0) continue;
      el.scrollTop = entries[i].top;
    }
  }

  /* Describe the focused control well enough to find its twin after a render,
     so keyboard and screen reader users are not dumped back to <body>. */
  function describeFocus() {
    var el = document.activeElement;
    if (!el || el === document.body || typeof el.closest !== 'function') return null;
    var scroller = drawerScroller();
    var inDrawer = !!scroller && scroller.contains(el);
    var inPage = !inDrawer && !!el.closest('#main-cart-items, #main-cart-footer');
    if (!inDrawer && !inPage) return null;

    if (el.hasAttribute('data-novapix-bundle-step')) {
      var qtyRoot = el.closest('[data-novapix-bundle-qty]');
      return {
        bundleStep: el.getAttribute('data-novapix-bundle-step'),
        bundleQty: qtyRoot && qtyRoot.getAttribute('data-novapix-bundle-qty')
      };
    }
    if (el.hasAttribute('data-novapix-remove-variant')) {
      return { removeVariant: el.getAttribute('data-novapix-remove-variant') };
    }
    if (el.name === 'plus' || el.name === 'minus') {
      var row = el.closest('[data-index]');
      return { stepName: el.name, rowIndex: row && row.getAttribute('data-index') };
    }
    if (el.id) return { id: el.id };
    return null;
  }

  function refocus(described) {
    if (!described) return;
    var el = null;
    if (described.bundleStep && described.bundleQty) {
      var qtyRoot = document.querySelector('[data-novapix-bundle-qty="' + described.bundleQty + '"]');
      el = qtyRoot && qtyRoot.querySelector('[data-novapix-bundle-step="' + described.bundleStep + '"]');
    } else if (described.removeVariant) {
      el = document.querySelector('[data-novapix-remove-variant="' + described.removeVariant + '"]');
    } else if (described.stepName && described.rowIndex) {
      var row = document.querySelector('[data-index="' + described.rowIndex + '"]');
      el = row && row.querySelector('[name="' + described.stepName + '"]');
    } else if (described.id) {
      el = document.getElementById(described.id);
    }
    if (el && el !== document.activeElement) {
      try { el.focus({ preventScroll: true }); } catch (err) { try { el.focus(); } catch (err2) {} }
    }
  }

  /* Repaint the drawer and the header badge from the sections the update
     returned, so the customer never sees a cart that disagrees with the
     reconciled one.

     `host` and `from` are NOT the same selector, however interchangeable they
     look. `host` is where the markup lives on the page; `from` is where the
     replacement lives inside the returned section HTML, which Shopify wraps in
     a .shopify-section div. They happen to coincide for the drawer, and they
     cannot for the badge: the page has <a id="cart-icon-bubble"> in
     sections/header.liquid, while sections/cart-icon-bubble.liquid renders only
     that anchor's CONTENTS. Using one selector for both looked up
     #cart-icon-bubble in markup that has no such element, found nothing, and
     skipped the badge without a word, so the count went stale after every
     reconcile and after clearing the cart. Shrine's own getSectionsToRender
     keeps the two apart for exactly this reason.

     Replacing the anchor's innerHTML rather than the anchor itself is what
     Shrine and Dawn both do, so nothing bound to that element is lost. The
     section renders the icon as well as the count, so the empty and full cart
     glyphs swap with it. */
  function repaint(data) {
    if (!data || !data.sections) return;

    // preserve scroll and focus across the swap: same task, so neither the
    // clamp on .drawer__inner nor the destruction of .cart-drawer__body ever
    // becomes visible
    var keepScrolls = snapshotScrolls();
    var keepFocus = describeFocus();

    var targets = [
      { section: 'cart-drawer', host: '.drawer__inner', from: '.drawer__inner' },
      { section: 'cart-icon-bubble', host: '#cart-icon-bubble', from: '.shopify-section' }
    ];
    // /cart page sections, only present on that template (see sectionsParam)
    var itemsSection = pageSection('main-cart-items');
    if (itemsSection) {
      targets.push({ section: itemsSection, host: '#main-cart-items', from: '#main-cart-items' });
    }
    var footerSection = pageSection('main-cart-footer');
    if (footerSection) {
      targets.push({ section: footerSection, host: '#main-cart-footer', from: '#main-cart-footer' });
    }
    targets.forEach(function (t) {
      var html = data.sections[t.section];
      var host = document.querySelector(t.host);
      if (!html || !host) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(t.from);
      if (parsed) host.innerHTML = parsed.innerHTML;
    });
    syncEmptyState(data);
    // a repaint mid-burst must not visually revert an optimistic count
    stampAllBursts();

    restoreScrolls(keepScrolls, false);
    refocus(keepFocus);

    /* The /cart page's empty-state chrome hangs off elements outside the
       repainted sections. A pass that empties the cart there (the last
       Lightning handset going, taking its adapter with it) reloads once. */
    if (data.item_count === 0 && document.querySelector('cart-items') &&
        window.location && typeof window.location.reload === 'function') {
      window.location.reload();
    }
  }

  /* `is-empty` lives on <cart-drawer>, outside the .drawer__inner we replace,
     so repainting alone would leave an emptied cart showing a header and an
     empty item list instead of the empty state. */
  function syncEmptyState(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (cart.item_count === 0) drawer.classList.add('is-empty');
    else drawer.classList.remove('is-empty');
  }

  // Callers awaiting a pass that had to queue behind a running one. Resolved
  // when the chain quiesces, so "await reconcile()" always means the work is
  // actually done, including work that arrived mid-pass.
  var reconcileWaiters = [];

  function reconcile() {
    if (running) {
      rerun = true;
      return new Promise(function (resolve) { reconcileWaiters.push(resolve); });
    }
    running = true;

    return getCart()
      .then(function (cart) {
        if (!cart || !cart.items) return null;

        // Consume any pending bundle edits in THIS pass: take a snapshot, then
        // clear the shared map so a burst arriving mid-write queues a fresh
        // pass (via running/rerun) instead of being half-applied twice.
        var targets = bundleTargets;
        bundleTargets = {};

        // The gift rule must judge the cart AS IT WILL BE once the bundle
        // edits land, or stepping 1 -> 2 would fail to add the trio in the
        // same write and stepping 2 -> 1 would fail to withdraw it.
        var effectivePanels = panelCount(cart);
        Object.keys(targets).forEach(function (key) {
          var id = Number(key);
          if (PANELS.indexOf(id) !== -1) {
            effectivePanels += targets[key] - qtyOf(cart, id);
          }
        });

        var want = requiredSet(cart, paidIntent, true, effectivePanels);
        var updates = {};

        // Bundle edits first: the variant's lines collapse onto the first at
        // the new total (the discount may have split them), the rest to zero.
        Object.keys(targets).forEach(function (key) {
          var id = Number(key);
          var total = Math.max(0, Math.floor(targets[key]));
          var lines = linesFor(cart, id);
          if (!lines.length) {
            if (total > 0) updates[id] = total;
            return;
          }
          // A target equal to the current total is a no-op, NOT a collapse:
          // the split is the discount engine's own arrangement, and rewriting
          // it would turn every net-zero burst into a pointless write.
          var current = lines.reduce(function (n, l) { return n + l.quantity; }, 0);
          if (current === total) return;
          lines.forEach(function (line, index) {
            var target = index === 0 ? total : 0;
            if (line.quantity !== target) updates[line.key] = target;
          });
        });

        ALL_GIFT.forEach(function (id) {
          var lines = linesFor(cart, id);
          var need = ids(want, id) ? 1 : 0;

          if (lines.length === 0) {
            // adding: keyed by variant id, which /cart/update.js accepts for
            // variants not yet in the cart
            if (need === 1) updates[id] = 1;
            return;
          }

          // Existing lines are keyed by line item key. When one variant somehow
          // occupies several lines, updating by variant id would only touch the
          // first, so each line is addressed explicitly.
          lines.forEach(function (line, index) {
            var target = index === 0 ? need : 0;
            if (line.quantity !== target) updates[line.key] = target;
          });
        });

        /* Pack sold lines round UP to the next whole pack. Rounding up is what
           lets repeat adds work without remembering anything: Shrine adds 1 to
           a cart that is already on a pack boundary, so 0 -> 1 -> 3, then
           3 -> 4 -> 6. Two adds, two packs. Rounding down or to nearest would
           swallow the second pack and, worse, delete the first add outright. */
        Object.keys(PACKS).forEach(function (key) {
          var id = Number(key);
          var size = PACKS[key];
          var lines = linesFor(cart, id);
          if (!lines.length) return; // absent stays absent

          var total = lines.reduce(function (n, l) { return n + l.quantity; }, 0);
          var want = Math.ceil(total / size) * size;

          // Collapse onto the first line, the same way the gift pieces do, so a
          // variant split across several lines still lands on one whole pack.
          lines.forEach(function (line, index) {
            var target = index === 0 ? want : 0;
            if (line.quantity !== target) updates[line.key] = target;
          });
        });

        // Companion counts follow their handset counts, in the same write:
        // adapter == Lightning handsets, cable == Bluetooth handsets.
        var companionKeys = [];
        COMPANIONS.forEach(function (rule) {
          companionKeys = companionKeys.concat(companionUpdates(cart, updates, rule));
        });

        if (!Object.keys(updates).length) return null;
        return request('/cart/update.js', { updates: updates, sections: sectionsParam() })
          .then(function (data) {
            /* Shopify answers an unaddable line (an unavailable variant) with
               a JSON status, not a network failure. If a companion entry is
               what sank the write, resend without ALL of them so the gift
               and pack corrections in the same request still land. The
               status names no key, so per-rule elimination would need extra
               writes; dropping the union costs one resend and loses nothing,
               because every mutation reconciles again. */
            if (data && data.status && companionKeys.length) {
              var rest = {};
              Object.keys(updates).forEach(function (key) {
                if (companionKeys.indexOf(key) === -1) rest[key] = updates[key];
              });
              if (window.console && console.warn) {
                console.warn('Novapix cart: companion update rejected', data.description || data.message || data.status);
              }
              if (!Object.keys(rest).length) return null;
              return request('/cart/update.js', { updates: rest, sections: sectionsParam() });
            }
            return data;
          });
      })
      .then(function (data) {
        if (data) repaint(data);
      })
      .catch(function () {
        /* never let cart upkeep break the page */
      })
      .finally(function () {
        running = false;
        if (rerun) {
          rerun = false;
          reconcile();
        } else {
          var settled = reconcileWaiters;
          reconcileWaiters = [];
          settled.forEach(function (resolve) { resolve(); });
        }
      });
  }

  /* The buy box's checkbox writes here. Sticky, so a reconcile triggered later
     by Shrine's add still knows what the customer chose. */
  function setPaidIntent(wanted) {
    paidIntent = wanted === null ? null : !!wanted;
    return reconcile();
  }

  // ------------------------------------------------------------- clear cart

  /* Empty the cart outright.

     Shrine has no such feature: the drawer schema has no block for it, and
     routes.cart_clear_url is handed to the bundle without anything calling it.
     The control is rendered by snippets/cart-drawer.liquid; this is the only
     thing that acts on it.

     It lives in this file because this module already owns every cart
     mutation. Clearing from anywhere else would leave paidIntent set, and the
     next panel added would silently bring back a gift pair the customer never
     asked for on this cart. */
  function clearCart() {
    paidIntent = false;
    if (typeof onPaidCleared === 'function') onPaidCleared();

    // The cart page renders its items from sections this module does not
    // repaint, so there it reloads rather than leaving stale rows on screen.
    // The drawer, which is the store's actual cart UI, repaints in place.
    var onCartPage = !!document.querySelector('cart-items');

    return request(route('cart/clear.js'), { sections: sectionsParam() })
      .then(function (data) {
        repaint(data);
        if (onCartPage && window.location && typeof window.location.reload === 'function') {
          window.location.reload();
          return null;
        }
        // Empty cart, so this settles on "no gift pieces" and changes nothing.
        // Run it anyway: the invariant is meant to hold after every mutation,
        // including this one, rather than only after the ones we predict.
        return reconcile();
      })
      .catch(function () {
        /* never let cart upkeep break the page */
      });
  }

  /* Set a variant's TOTAL quantity across every line it occupies, in one
     write: the first line takes the total, the rest go to zero.

     This is how the aggregated bundle row in the cart is edited. The BXGY gift
     discount can split a panel variant across several lines, and Shrine's own
     stepper addresses lines by POSITION and validates the response against the
     request, which is exactly what mis-fired on split lines (the wrong line
     grew and the mismatch rendered a phantom "You can only add N" error).
     Addressing by key and setting the total makes both impossible. Zero
     removes the whole bundle. The gift set follows the new panel count via
     reconcile, as it does for every other mutation. */
  function setVariantQuantity(variantId, total) {
    bundleTargets[variantId] = Math.max(0, Math.floor(total));
    // reconcile consumes the target and folds the panel change, the gift
    // correction it causes, and any pack correction into ONE write
    return reconcile();
  }

  function removeVariant(variantId) {
    return setVariantQuantity(variantId, 0);
  }

  // -------------------------------------------------- optimistic bundle steps

  /* Click-to-feedback must be instant even though the write is not. Each click
     moves an in-memory target and stamps it onto the rendered inputs
     immediately; a ~450ms debounce then flushes the WHOLE burst as one
     setVariantQuantity. Fast-stepping 3 -> 8 costs one write, not five, and no
     click is ever swallowed. The row's own Shrine loading spinner is unhidden
     while the flush is pending, which also gets base.css's built-in 50% dim on
     the stale price, so the money shown is always the last CONFIRMED total. */
  var bundleBursts = {}; // variant id -> { target, timer }
  var BURST_FLUSH_MS = 450;

  function burstNodes(variantId) {
    return document.querySelectorAll('[data-novapix-bundle-qty="' + variantId + '"]');
  }

  function setRowPending(qtyRoot, pending) {
    var row = qtyRoot.closest('.cart-drawer-item, .cart-item');
    if (!row) return;
    var overlays = row.querySelectorAll('.cart-item__totals .loading-overlay');
    for (var i = 0; i < overlays.length; i++) {
      overlays[i].classList[pending ? 'remove' : 'add']('hidden');
    }
  }

  function stampBurst(variantId) {
    var burst = bundleBursts[variantId];
    if (!burst) return;
    var nodes = burstNodes(variantId);
    for (var i = 0; i < nodes.length; i++) {
      var input = nodes[i].querySelector('.quantity__input');
      if (input) input.value = String(burst.target);
      setRowPending(nodes[i], true);
    }
  }

  /* Repaint replaces the rendered rows wholesale, so a repaint landing while a
     burst is still pending would visually revert the count to the server's;
     re-stamping afterwards keeps the optimistic number on screen. */
  function stampAllBursts() {
    Object.keys(bundleBursts).forEach(function (id) { stampBurst(Number(id)); });
  }

  function clearBurstPending(variantId) {
    var nodes = burstNodes(variantId);
    for (var i = 0; i < nodes.length; i++) setRowPending(nodes[i], false);
  }

  function flushBurst(variantId) {
    var burst = bundleBursts[variantId];
    if (!burst) return;
    burst.timer = null;
    var flushed = burst.target;

    setVariantQuantity(variantId, flushed).then(function () {
      var current = bundleBursts[variantId];
      // more clicks arrived while the write was in flight: their debounce
      // timer is already ticking and will flush again; leave the burst alone
      if (!current || current.timer || current.target !== flushed) return;

      delete bundleBursts[variantId];
      // a write repaints and the fresh render is clean; a net-zero burst
      // writes nothing and repaints nothing, so un-dim explicitly
      clearBurstPending(variantId);

      // Reaching empty on the /cart page: the empty-state chrome hangs off
      // elements outside the repainted sections, so reload once.
      if (flushed === 0 && document.querySelector('cart-items') &&
          window.location && typeof window.location.reload === 'function') {
        getCart().then(function (cart) {
          if (cart && cart.item_count === 0) window.location.reload();
        });
      }
    });
  }

  /* One delegated listener rather than a per-element one, because the drawer
     markup is replaced wholesale on every cart update and any handler bound to
     the old nodes would go with it. */
  function onDocumentClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    /* The bundle row's +/- . Optimistic: the number changes NOW, every click
       counts, and the debounced flush writes the burst's final total in one
       request. No cart read on click, so feedback is instant even through the
       dev-server proxy. */
    var stepBtn = target.closest('[data-novapix-bundle-step]');
    if (stepBtn) {
      event.preventDefault();
      var qtyRoot = stepBtn.closest('[data-novapix-bundle-qty]');
      if (!qtyRoot) return;
      var stepVariant = Number(qtyRoot.getAttribute('data-novapix-bundle-qty'));
      var step = Number(stepBtn.getAttribute('data-novapix-bundle-step')) || 0;

      var burst = bundleBursts[stepVariant];
      if (!burst) {
        // the rendered value is the latest server truth: the module's own
        // repaints keep it fresh, and completed bursts are stamped from it
        var input = qtyRoot.querySelector('.quantity__input');
        burst = bundleBursts[stepVariant] = {
          target: Math.max(0, Number(input && input.value) || 0),
          timer: null
        };
      }
      burst.target = Math.max(0, burst.target + step);
      stampBurst(stepVariant);

      if (burst.timer) clearTimeout(burst.timer);
      burst.timer = setTimeout(function () { flushBurst(stepVariant); }, BURST_FLUSH_MS);
      return;
    }

    var removeBtn = target.closest('[data-novapix-remove-variant]');
    if (removeBtn) {
      event.preventDefault();
      if (removeBtn.getAttribute('data-busy') === 'true') return;
      removeBtn.setAttribute('data-busy', 'true');
      var variantId = Number(removeBtn.getAttribute('data-novapix-remove-variant'));
      removeVariant(variantId).then(function () {
        // The repaint usually replaced this button already; if not, re-arm it.
        removeBtn.setAttribute('data-busy', 'false');
        // The /cart page renders its rows from sections this module does not
        // repaint, same situation as clearCart.
        if (document.querySelector('cart-items') &&
            window.location && typeof window.location.reload === 'function') {
          window.location.reload();
        }
      });
      return;
    }

    var root = target.closest('[data-novapix-clear]');
    if (!root) {
      // clicking anywhere else abandons a confirm that was left open
      var open = document.querySelectorAll('[data-novapix-clear][data-state="confirm"]');
      for (var i = 0; i < open.length; i++) open[i].setAttribute('data-state', 'idle');
      return;
    }

    if (target.closest('[data-novapix-clear-trigger]')) {
      event.preventDefault();
      root.setAttribute('data-state', 'confirm');
      return;
    }

    if (target.closest('[data-novapix-clear-no]')) {
      event.preventDefault();
      root.setAttribute('data-state', 'idle');
      return;
    }

    if (target.closest('[data-novapix-clear-yes]')) {
      event.preventDefault();
      if (root.getAttribute('data-busy') === 'true') return;
      root.setAttribute('data-busy', 'true');
      clearCart().then(function () {
        root.setAttribute('data-busy', 'false');
        root.setAttribute('data-state', 'idle');
      });
    }
  }

  // ---------------------------------------------------------------- triggers

  // Observing fetch catches every cart mutation whoever makes it: Shrine's
  // product form, the cart drawer, quantity steppers, or another script. That
  // is what makes "no path reaches checkout with the wrong set" true rather
  // than a claim about the paths we happen to know about.
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isOwn = ownRequests ? !!(init && ownRequests.has(init)) : internal;
      var isMutation = !isOwn && CART_MUTATIONS.test(url);

      /* SHRINE's own renders (charger stepper, gift remove, add-to-cart while
         the drawer is open) also replace .drawer__inner and lose the scroll
         position and focus. The vendor bundle is not editable, but every one
         of those flows starts with a cart request that passes through here:
         capture at request time, then put things back on a short deferral
         after its render, and ONLY if the position was clamped to 0 or focus
         fell to <body>, so a customer who scrolled or refocused meanwhile is
         never fought. */
      var keepScrolls = null;
      var keepFocus = null;
      if (isMutation) {
        keepScrolls = snapshotScrolls();
        keepFocus = describeFocus();
      }

      var result = nativeFetch.apply(this, arguments);
      if (isMutation) {
        result.then(function () {
          [0, 80, 220].forEach(function (delay) {
            setTimeout(function () {
              if (keepScrolls) restoreScrolls(keepScrolls, true);
              if (keepFocus && document.activeElement === document.body) refocus(keepFocus);
            }, delay);
          });
          reconcile();
        }).catch(function () {});
      }
      return result;
    };
  }

  document.addEventListener('click', onDocumentClick);

  function init() { reconcile(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // A bfcache restore (back button) fires pageshow, not DOMContentLoaded.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) reconcile();
  });

  window.NovapixCart = {
    reconcile: reconcile,
    setPaidIntent: setPaidIntent,
    getPaidIntent: function () { return paidIntent; },
    onPaidCleared: function (fn) { onPaidCleared = fn; },
    clearCart: clearCart,
    removeVariant: removeVariant,
    setVariantQuantity: setVariantQuantity,
    requiredSet: requiredSet,
    panelCount: panelCount,
    qtyOf: qtyOf,
    countWhere: countWhere,
    isLightningLine: isLightningLine,
    lightningCount: lightningCount,
    bluetoothCount: bluetoothCount,
    adapterUpdates: adapterUpdates,
    cableUpdates: cableUpdates,
    powerBrickUpdates: powerBrickUpdates,
    companionUpdates: companionUpdates,
    PANELS: PANELS,
    PAID: PAID,
    FREE: FREE,
    PACKS: PACKS,
    LIGHTNING_PROPERTY: LIGHTNING_PROPERTY,
    LIGHTNING_VALUE: LIGHTNING_VALUE,
    ADAPTER: ADAPTER,
    BLUETOOTH: BLUETOOTH,
    CABLE: CABLE,
    POWER_BRICK: POWER_BRICK,
    COMPANIONS: COMPANIONS
  };
})();
